import { useState, useEffect, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  User,
  GraduationCap,
  Microscope,
  ChevronRight,
  ChevronLeft,
  Check,
  Pencil,
  Save,
  BookOpen,
  FlaskConical,
  Globe,
  Eye,
  BarChart3,
  Sparkles,
  Building,
  FileText,
  Award,
} from 'lucide-react';
import { UCI_MAJORS, ETHNICITIES, SCORE_TIERS } from '@apex/shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';

/* ─── animation variants ─── */
const pageVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const stepVariant = {
  enter: (dir: number) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit: (dir: number) => ({ x: dir > 0 ? -80 : 80, opacity: 0, transition: { duration: 0.25 } }),
};

/* ─── helper: get tier for score ─── */
function getTier(score: number) {
  return SCORE_TIERS.find((t) => score >= t.min && score <= t.max) ?? SCORE_TIERS[SCORE_TIERS.length - 1];
}

/* ─── select options ─── */
const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

const YEARS = [
  { value: 'freshman', label: 'Freshman' },
  { value: 'sophomore', label: 'Sophomore' },
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
  { value: 'graduate', label: 'Graduate' },
  { value: 'postdoc', label: 'Postdoc' },
] as const;

/* ════════════════════════════════════════════════════════════
   PROFILE PAGE
   ═══════════════════════════════════════════════════════════ */
export default function ProfilePage() {
  const { user, setUser } = useAuthStore();

  if (!user) return null;

  const isStudent = user.role === 'student';
  const needsOnboarding = !user.onboarding_completed;

  return (
    <motion.div variants={pageVariant} initial="hidden" animate="visible">
      {needsOnboarding ? (
        isStudent ? <StudentOnboarding /> : <ResearcherOnboarding />
      ) : (
        isStudent ? <StudentProfileView /> : <ResearcherProfileView />
      )}
    </motion.div>
  );
}

/* ════════════════════════════════════════════════════════════
   STUDENT ONBOARDING WIZARD
   ═══════════════════════════════════════════════════════════ */
function StudentOnboarding() {
  const { user, setUser } = useAuthStore();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const totalSteps = 3;

  /* form state */
  const [age, setAge] = useState<number | ''>('');
  const [gender, setGender] = useState('');
  const [year, setYear] = useState('');
  const [major, setMajor] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [nativeEnglish, setNativeEnglish] = useState<boolean | null>(null);
  const [visionCorrection, setVisionCorrection] = useState<boolean | null>(null);

  const goNext = () => { setDir(1); setStep((s) => Math.min(s + 1, totalSteps - 1)); };
  const goPrev = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      const data = {
        age: age || null,
        gender: gender || null,
        year: year || null,
        major: major || null,
        ethnicity: ethnicity || null,
        is_native_english_speaker: nativeEnglish,
        has_vision_correction: visionCorrection,
      };
      const updated = await api.updateStudentProfile(data);
      // Also mark onboarding completed
      const updatedUser = await api.updateUser({ onboarding_completed: true });
      setUser({ ...user!, ...updatedUser, onboarding_completed: true, student_profile: updated });
      toast.success('Profile completed! Welcome to APEX.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 shadow-lg shadow-brand-500/25 mb-4">
          <GraduationCap className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Complete Your Profile</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Help us match you with the right research studies
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  i < step
                    ? 'bg-brand-500 text-white shadow-md shadow-brand-500/30'
                    : i === step
                    ? 'bg-gradient-to-br from-brand-500 to-accent-500 text-white shadow-lg shadow-brand-500/30 scale-110'
                    : 'bg-gray-200 dark:bg-gray-800 text-gray-400'
                }`}
              >
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < totalSteps - 1 && (
                <div className="hidden sm:block w-16 lg:w-24 h-1 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                  <motion.div
                    className="h-full bg-brand-500 rounded-full"
                    initial={false}
                    animate={{ width: i < step ? '100%' : '0%' }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Basic Info</span>
          <span>Academic</span>
          <span>Additional</span>
        </div>
      </div>

      {/* Step content */}
      <div className="card p-6 sm:p-8 overflow-hidden min-h-[320px] relative">
        <AnimatePresence mode="wait" custom={dir}>
          {step === 0 && (
            <motion.div
              key="step0"
              variants={stepVariant}
              custom={dir}
              initial="enter"
              animate="center"
              exit="exit"
              className="space-y-5"
            >
              <h3 className="text-lg font-semibold">Basic Information</h3>

              {/* Age */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Age</label>
                <input
                  type="number"
                  min={16}
                  max={100}
                  placeholder="e.g. 20"
                  value={age}
                  onChange={(e) => setAge(e.target.value ? parseInt(e.target.value) : '')}
                  className="input-field"
                />
              </div>

              {/* Gender */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Gender</label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select gender</option>
                  {GENDERS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>

              {/* Academic Year */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Academic Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select year</option>
                  {YEARS.map((y) => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </select>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step1"
              variants={stepVariant}
              custom={dir}
              initial="enter"
              animate="center"
              exit="exit"
              className="space-y-5"
            >
              <h3 className="text-lg font-semibold">Academic Details</h3>

              {/* Major */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Major</label>
                <select
                  value={major}
                  onChange={(e) => setMajor(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select major</option>
                  {UCI_MAJORS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {/* Ethnicity */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ethnicity</label>
                <select
                  value={ethnicity}
                  onChange={(e) => setEthnicity(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select ethnicity</option>
                  {ETHNICITIES.map((eth) => (
                    <option key={eth} value={eth}>{eth}</option>
                  ))}
                </select>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              variants={stepVariant}
              custom={dir}
              initial="enter"
              animate="center"
              exit="exit"
              className="space-y-5"
            >
              <h3 className="text-lg font-semibold">Additional Information</h3>

              {/* Native English */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Are you a native English speaker?
                </label>
                <div className="flex gap-3">
                  <ToggleButton
                    active={nativeEnglish === true}
                    onClick={() => setNativeEnglish(true)}
                    label="Yes"
                  />
                  <ToggleButton
                    active={nativeEnglish === false}
                    onClick={() => setNativeEnglish(false)}
                    label="No"
                  />
                </div>
              </div>

              {/* Vision Correction */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Do you wear glasses or contacts (vision correction)?
                </label>
                <div className="flex gap-3">
                  <ToggleButton
                    active={visionCorrection === true}
                    onClick={() => setVisionCorrection(true)}
                    label="Yes"
                  />
                  <ToggleButton
                    active={visionCorrection === false}
                    onClick={() => setVisionCorrection(false)}
                    label="No"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={goPrev}
            disabled={step === 0}
            className="btn-ghost disabled:opacity-0"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {step < totalSteps - 1 ? (
            <button type="button" onClick={goNext} className="btn-primary">
              Continue
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleComplete}
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </div>
              ) : (
                <>
                  Complete Profile
                  <Sparkles className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   RESEARCHER ONBOARDING
   ═══════════════════════════════════════════════════════════ */
function ResearcherOnboarding() {
  const { user, setUser } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [department, setDepartment] = useState('');
  const [labName, setLabName] = useState('');
  const [irbNumber, setIrbNumber] = useState('');
  const [institution, setInstitution] = useState('University of California, Irvine');

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      const data = {
        department: department || null,
        lab_name: labName || null,
        irb_number: irbNumber || null,
        institution: institution || null,
      };
      const updated = await api.updateResearcherProfile(data);
      const updatedUser = await api.updateUser({ onboarding_completed: true });
      setUser({ ...user!, ...updatedUser, onboarding_completed: true, researcher_profile: updated });
      toast.success('Profile completed! Start creating studies.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500 to-brand-500 shadow-lg shadow-accent-500/25 mb-4">
          <Microscope className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Set Up Your Research Profile</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Add your lab details so participants can learn about your research
        </p>
      </div>

      <div className="card p-6 sm:p-8 space-y-5">
        {/* Department */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
          <div className="relative">
            <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="e.g. Cognitive Sciences"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="input-field pl-11"
            />
          </div>
        </div>

        {/* Lab Name */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Lab Name</label>
          <div className="relative">
            <FlaskConical className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="e.g. Human Perception Lab"
              value={labName}
              onChange={(e) => setLabName(e.target.value)}
              className="input-field pl-11"
            />
          </div>
        </div>

        {/* IRB Number */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IRB Number</label>
          <div className="relative">
            <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="e.g. HS-2024-1234"
              value={irbNumber}
              onChange={(e) => setIrbNumber(e.target.value)}
              className="input-field pl-11"
            />
          </div>
        </div>

        {/* Institution */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Institution</label>
          <div className="relative">
            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="University of California, Irvine"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              className="input-field pl-11"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={handleComplete}
            disabled={isSubmitting}
            className="btn-primary w-full"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </div>
            ) : (
              <>
                Complete Profile
                <Sparkles className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   STUDENT PROFILE VIEW / EDIT
   ═══════════════════════════════════════════════════════════ */
function StudentProfileView() {
  const { user, setUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const profile = user?.student_profile;
  const tier = getTier(user?.reliability_score ?? 3.0);
  const score = user?.reliability_score ?? 3.0;

  /* edit state */
  const [age, setAge] = useState<number | ''>(profile?.age ?? '');
  const [gender, setGender] = useState(profile?.gender ?? '');
  const [year, setYear] = useState(profile?.year ?? '');
  const [major, setMajor] = useState(profile?.major ?? '');
  const [ethnicity, setEthnicity] = useState(profile?.ethnicity ?? '');
  const [nativeEnglish, setNativeEnglish] = useState<boolean | null>(profile?.is_native_english_speaker ?? null);
  const [visionCorrection, setVisionCorrection] = useState<boolean | null>(profile?.has_vision_correction ?? null);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const data = {
        age: age || null,
        gender: gender || null,
        year: year || null,
        major: major || null,
        ethnicity: ethnicity || null,
        is_native_english_speaker: nativeEnglish,
        has_vision_correction: visionCorrection,
      };
      const updated = await api.updateStudentProfile(data);
      setUser({ ...user!, student_profile: updated });
      setEditing(false);
      toast.success('Profile updated!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayItems = [
    { label: 'Age', value: profile?.age ?? '—', icon: User },
    { label: 'Gender', value: GENDERS.find((g) => g.value === profile?.gender)?.label ?? '—', icon: User },
    { label: 'Academic Year', value: YEARS.find((y) => y.value === profile?.year)?.label ?? '—', icon: GraduationCap },
    { label: 'Major', value: profile?.major ?? '—', icon: BookOpen },
    { label: 'Ethnicity', value: profile?.ethnicity ?? '—', icon: Globe },
    { label: 'Native English Speaker', value: profile?.is_native_english_speaker == null ? '—' : profile.is_native_english_speaker ? 'Yes' : 'No', icon: Globe },
    { label: 'Vision Correction', value: profile?.has_vision_correction == null ? '—' : profile.has_vision_correction ? 'Yes' : 'No', icon: Eye },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-header">My Profile</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your participant profile and track your reliability
          </p>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="btn-secondary text-sm">
            <Pencil className="w-4 h-4" />
            Edit
          </button>
        )}
      </div>

      {/* Reliability Score Card */}
      <div className="card p-6 relative overflow-hidden">
        {/* Subtle glow */}
        <div
          className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: tier.color }}
        />

        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* Score badge */}
          <div className="flex-shrink-0">
            <div
              className="w-20 h-20 rounded-2xl flex flex-col items-center justify-center shadow-xl"
              style={{ backgroundColor: `${tier.color}20`, border: `2px solid ${tier.color}40` }}
            >
              <span className="text-2xl font-bold" style={{ color: tier.color }}>
                {score.toFixed(1)}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: tier.color }}>
                / 5.0
              </span>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-4 h-4" style={{ color: tier.color }} />
              <span className="text-lg font-bold" style={{ color: tier.color }}>
                {tier.emoji} {tier.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Your reliability score determines which studies you can access. Keep it high!
            </p>

            {/* Score bar */}
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: tier.color }}
                initial={{ width: 0 }}
                animate={{ width: `${(score / 5) * 100}%` }}
                transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
              />
            </div>

            {/* Stats row */}
            <div className="flex gap-6 mt-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <BarChart3 className="w-3.5 h-3.5 text-success-500" />
                <span><strong className="text-gray-700 dark:text-gray-300">{user?.total_completed ?? 0}</strong> completed</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <BarChart3 className="w-3.5 h-3.5 text-danger-500" />
                <span><strong className="text-gray-700 dark:text-gray-300">{user?.total_no_shows ?? 0}</strong> no-shows</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <BarChart3 className="w-3.5 h-3.5 text-warning-500" />
                <span><strong className="text-gray-700 dark:text-gray-300">{user?.total_cancellations ?? 0}</strong> cancelled</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile info card */}
      <AnimatePresence mode="wait">
        {editing ? (
          <motion.div
            key="edit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-6 sm:p-8 space-y-5"
          >
            <h3 className="text-lg font-semibold">Edit Profile</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Age</label>
                <input
                  type="number"
                  min={16}
                  max={100}
                  value={age}
                  onChange={(e) => setAge(e.target.value ? parseInt(e.target.value) : '')}
                  className="input-field"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Gender</label>
                <select value={gender} onChange={(e) => setGender(e.target.value)} className="input-field">
                  <option value="">Select gender</option>
                  {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Academic Year</label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className="input-field">
                  <option value="">Select year</option>
                  {YEARS.map((y) => <option key={y.value} value={y.value}>{y.label}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Major</label>
                <select value={major} onChange={(e) => setMajor(e.target.value)} className="input-field">
                  <option value="">Select major</option>
                  {UCI_MAJORS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ethnicity</label>
                <select value={ethnicity} onChange={(e) => setEthnicity(e.target.value)} className="input-field">
                  <option value="">Select ethnicity</option>
                  {ETHNICITIES.map((eth) => <option key={eth} value={eth}>{eth}</option>)}
                </select>
              </div>
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Native English Speaker</label>
                <div className="flex gap-3">
                  <ToggleButton active={nativeEnglish === true} onClick={() => setNativeEnglish(true)} label="Yes" />
                  <ToggleButton active={nativeEnglish === false} onClick={() => setNativeEnglish(false)} label="No" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Vision Correction</label>
                <div className="flex gap-3">
                  <ToggleButton active={visionCorrection === true} onClick={() => setVisionCorrection(true)} label="Yes" />
                  <ToggleButton active={visionCorrection === false} onClick={() => setVisionCorrection(false)} label="No" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
              <button onClick={() => setEditing(false)} className="btn-ghost">
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="view"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-6 sm:p-8"
          >
            <h3 className="text-lg font-semibold mb-5">Profile Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {displayItems.map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-brand-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    <p className="text-sm font-medium mt-0.5">{String(item.value)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   RESEARCHER PROFILE VIEW / EDIT
   ═══════════════════════════════════════════════════════════ */
function ResearcherProfileView() {
  const { user, setUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const profile = user?.researcher_profile;

  const [department, setDepartment] = useState(profile?.department ?? '');
  const [labName, setLabName] = useState(profile?.lab_name ?? '');
  const [irbNumber, setIrbNumber] = useState(profile?.irb_number ?? '');
  const [institution, setInstitution] = useState(profile?.institution ?? '');

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const data = {
        department: department || null,
        lab_name: labName || null,
        irb_number: irbNumber || null,
        institution: institution || null,
      };
      const updated = await api.updateResearcherProfile(data);
      setUser({ ...user!, researcher_profile: updated });
      setEditing(false);
      toast.success('Profile updated!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayItems = [
    { label: 'Department', value: profile?.department ?? '—', icon: Building },
    { label: 'Lab Name', value: profile?.lab_name ?? '—', icon: FlaskConical },
    { label: 'IRB Number', value: profile?.irb_number ?? '—', icon: FileText },
    { label: 'Institution', value: profile?.institution ?? '—', icon: Globe },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-header">My Profile</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your researcher profile and lab information
          </p>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="btn-secondary text-sm">
            <Pencil className="w-4 h-4" />
            Edit
          </button>
        )}
      </div>

      {/* User summary card */}
      <div className="card p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500 to-brand-500 flex items-center justify-center text-white text-lg font-bold shadow-lg shadow-accent-500/20">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div>
            <h2 className="text-lg font-bold">{user?.first_name} {user?.last_name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <Microscope className="w-3.5 h-3.5 text-accent-500" />
              <span className="text-xs font-medium text-accent-500">Researcher</span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile info */}
      <AnimatePresence mode="wait">
        {editing ? (
          <motion.div
            key="edit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-6 sm:p-8 space-y-5"
          >
            <h3 className="text-lg font-semibold">Edit Profile</h3>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                <div className="relative">
                  <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="input-field pl-11"
                    placeholder="e.g. Cognitive Sciences"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Lab Name</label>
                <div className="relative">
                  <FlaskConical className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={labName}
                    onChange={(e) => setLabName(e.target.value)}
                    className="input-field pl-11"
                    placeholder="e.g. Human Perception Lab"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IRB Number</label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={irbNumber}
                    onChange={(e) => setIrbNumber(e.target.value)}
                    className="input-field pl-11"
                    placeholder="e.g. HS-2024-1234"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Institution</label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    className="input-field pl-11"
                    placeholder="University of California, Irvine"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
              <button onClick={() => setEditing(false)} className="btn-ghost">
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="view"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-6 sm:p-8"
          >
            <h3 className="text-lg font-semibold mb-5">Lab Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {displayItems.map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-accent-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    <p className="text-sm font-medium mt-0.5">{String(item.value)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════════ */
function ToggleButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all duration-200 ${
        active
          ? 'bg-brand-500/10 border-brand-500/40 text-brand-600 dark:text-brand-400 shadow-sm'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      {active && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
      {label}
    </button>
  );
}
